import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { storefront } from "../api/storefront";
import type { Outlet } from "../api/types";
import { nearestOutlet } from "../utils/geo";

/**
 * Точка доставки витрины (НСИ CDP «Точки доставки»).
 *
 * Раньше адрес был один и захардкожен в brand.ts. Теперь список точек приходит с BFF
 * (GET /api/v1/outlets), клиент выбирает точку в шапке, выбор запоминается в localStorage
 * (ключ vd_outlet), а `code` выбранной точки уходит в чекаут как outlet_id.
 *
 * При первом заходе (нет сохранённого выбора) пробуем определить ближайшую по геолокации;
 * если разрешения нет / браузер не поддерживает — берём первую точку из списка (не блокируем).
 */
const STORAGE_KEY = "vd_outlet";

function readStoredCode(): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function writeStoredCode(code: string | null): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (code) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* приватный режим / нет доступа — просто не запоминаем */
  }
}

export const useOutletStore = defineStore("outlet", () => {
  const outlets = ref<Outlet[]>([]);
  const selectedCode = ref<string | null>(readStoredCode());
  const loading = ref(false);
  const loaded = ref(false);
  const locating = ref(false);

  const selectedOutlet = computed<Outlet | null>(
    () => outlets.value.find((o) => o.code === selectedCode.value) ?? null,
  );

  function select(code: string): void {
    selectedCode.value = code;
    writeStoredCode(code);
  }

  /**
   * Ближайшая точка по геолокации браузера (гаверсинус). Геолокация опциональна: нет разрешения /
   * поддержки / таймаут → фолбэк на первую точку (если ещё ничего не выбрано). Никогда не бросает.
   */
  async function selectNearest(): Promise<void> {
    const first = outlets.value[0];
    const fallback = (): void => {
      if (!selectedOutlet.value && first) select(first.code);
    };
    const geo = typeof navigator !== "undefined" ? navigator.geolocation : undefined;
    if (!geo) {
      fallback();
      return;
    }
    locating.value = true;
    await new Promise<void>((resolve) => {
      geo.getCurrentPosition(
        (pos) => {
          const n = nearestOutlet(outlets.value, pos.coords.latitude, pos.coords.longitude);
          if (n) select(n.code);
          else fallback();
          resolve();
        },
        () => {
          fallback();
          resolve();
        },
        { timeout: 5000, maximumAge: 600_000, enableHighAccuracy: false },
      );
    }).finally(() => {
      locating.value = false;
    });
  }

  /**
   * Загрузить точки с BFF. Best-effort: сбой → пустой список (шапка покажет дефолтный адрес бренда).
   * Если сохранённый выбор ещё валиден — оставляем его; иначе пробуем ближайшую (гео опционально).
   */
  async function load(): Promise<void> {
    loading.value = true;
    try {
      const res = await storefront.outlets.list();
      outlets.value = res.outlets;
      loaded.value = true;
      const stillValid =
        selectedCode.value != null && outlets.value.some((o) => o.code === selectedCode.value);
      if (!stillValid) {
        selectedCode.value = null;
        if (outlets.value.length > 0) await selectNearest();
      }
    } catch {
      /* точки недоступны — шапка покажет дефолтный адрес бренда */
    } finally {
      loading.value = false;
    }
  }

  function reset(): void {
    outlets.value = [];
    selectedCode.value = readStoredCode();
    loading.value = false;
    loaded.value = false;
    locating.value = false;
  }

  return {
    outlets,
    selectedCode,
    selectedOutlet,
    loading,
    loaded,
    locating,
    select,
    selectNearest,
    load,
    reset,
  };
});
